import moment, { Moment } from 'moment';
import React, { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import { RRule } from 'rrule';

interface RepetitionOption {
  value: string;
  RRuleValue?: number;
  RRuleByweekday?: string[];
  RRuleBysetpos?: number[];
}

interface ConflictDetail {
  resource: string;
  resourceName: string;
  activeEventDates: [string, string];
  pendingEventDates: [string, string];
  occurrenceIndex: number;
}

interface GroupedConflict {
  occurrenceId: string;
  occurrenceIndex: number;
  pendingEventDates: [string, string];
  startDate: string;
  endDate: string;
  conflicts: ConflictDetail[];
}

interface EventData {
  id?: string;
  start: string | Date;
  end: string | Date;
  resource: string;
  active: boolean;
  title?: string;
  [key: string]: any;
}

interface ResourceData {
  id: string;
  name: string;
  [key: string]: any;
}

interface RecurringBookingContextType {
  isRecurringBooking: boolean;
  temporaryStartDate: string | Date | null;
  temporaryEndDate: string | Date | null;
  temporarySelectedDates: (string | Date)[];
  selectedRepetition: RepetitionOption | null;
  selectedOccurances: number;
  rule: RRule | null;
  conflicts: GroupedConflict[];
  setTemporaryStartDate: (date: string | Date | null) => void;
  setTemporaryEndDate: (date: string | Date | null) => void;
  setSelectedRepetition: (repetition: RepetitionOption | null) => void;
  setSelectedOccurances: (count: number) => void;
  debouncedSetTempDates: (dates: (string | Date)[]) => void;
  setEventDataContext: (data: EventData[]) => void;
  setRecoursesContext: (resources: ResourceData[]) => void;
  resetAll: () => void;
  getTrimmedRRuleString: () => string | null;
}

interface RecurringBookingProviderProps {
  originalSelectedStartDate: string | Date | null;
  originalSelectedEndDate: string | Date | null;
  children: ReactNode;
}

const RecurringBookingContext = createContext<RecurringBookingContextType | undefined>(undefined);

const useDebounce = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

export const RecurringBookingProvider: React.FC<RecurringBookingProviderProps> = ({
  originalSelectedStartDate,
  originalSelectedEndDate,
  children
}) => {
  const [temporaryStartDate, setTemporaryStartDate] = useState<string | Date | null>(originalSelectedStartDate);
  const [temporaryEndDate, setTemporaryEndDate] = useState<string | Date | null>(originalSelectedEndDate);
  const [temporarySelectedDates, setTemporarySelectedDates] = useState<(string | Date)[]>([]);
  const [selectedRepetition, setSelectedRepetition] = useState<RepetitionOption | null>(null);
  const [selectedOccurances, setSelectedOccurances] = useState<number>(2);
  const [rule, setRule] = useState<RRule | null>(null);
  const [conflicts, setConflicts] = useState<GroupedConflict[]>([]);
  const [eventData, setEventDataContext] = useState<EventData[]>([]);
  const [recources, setRecoursesContext] = useState<ResourceData[]>([]);

  const debouncedSetTempDates = useDebounce(setTemporarySelectedDates, 300);

  const isRecurringBooking = Boolean(selectedRepetition && selectedRepetition.value !== 'does_not_repeat');

  const createRRule = useCallback((): RRule | null => {
    if (!selectedRepetition || !selectedOccurances || selectedOccurances <= 0 || selectedRepetition.value === 'does_not_repeat') {
      return null;
    }

    return new RRule({
      freq: selectedRepetition.RRuleValue,
      count: selectedOccurances,
      byweekday: selectedRepetition.RRuleByweekday || undefined,
      bysetpos: selectedRepetition.RRuleBysetpos || undefined,
    });
  }, [selectedRepetition, selectedOccurances]);

  useEffect(() => {
    const newRule = createRRule();
    setRule(newRule);
  }, [selectedRepetition, selectedOccurances, createRRule]);

  const groupConflictsByOccurrence = (allConflictsWithNames: ConflictDetail[]): GroupedConflict[] => {
    const groupedConflicts = allConflictsWithNames.reduce((acc, conflict) => {
      const occurrenceKey = `${conflict.pendingEventDates[0]}_${conflict.pendingEventDates[1]}`;

      if (!acc[occurrenceKey]) {
        acc[occurrenceKey] = {
          occurrenceId: occurrenceKey,
          occurrenceIndex: conflict.occurrenceIndex,
          pendingEventDates: conflict.pendingEventDates,
          startDate: conflict.pendingEventDates[0],
          endDate: conflict.pendingEventDates[1],
          conflicts: []
        };
      }

      acc[occurrenceKey].conflicts.push({
        resource: conflict.resource,
        resourceName: conflict.resourceName,
        activeEventDates: conflict.activeEventDates,
        pendingEventDates: conflict.pendingEventDates,
        occurrenceIndex: conflict.occurrenceIndex
      });

      return acc;
    }, {} as Record<string, GroupedConflict>);

    return Object.values(groupedConflicts)
      .sort((a, b) => {
        if (a.occurrenceIndex !== b.occurrenceIndex) {
          return a.occurrenceIndex - b.occurrenceIndex;
        }
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });
  };

  const calculateDurationInHours = (startTime: string | Date, endTime: string | Date): number => {
    const start = moment(startTime);
    const end = moment(endTime);
    return end.diff(start, 'hours', true);
  };

  const isRecurringBookingConflicting = useCallback((
    pendingBookingStartDate: Moment,
    pendingBookingEndDate: Moment,
    pendingResource: string
  ): ConflictDetail[] => {
    const conflicts: ConflictDetail[] = [];
    const activeEventData = eventData.filter(event => event.active);

    activeEventData.forEach(activeEvent => {
      if (activeEvent.resource !== pendingResource) {
        return;
      }

      const activeEventStart = moment(activeEvent.start);
      const activeEventEnd = moment(activeEvent.end);

      const notOverlapping = pendingBookingEndDate.isSameOrBefore(activeEventStart) ||
        pendingBookingStartDate.isSameOrAfter(activeEventEnd);
      const isOverlapping = !notOverlapping;

      if (isOverlapping) {
        conflicts.push({
          resource: activeEvent.resource,
          resourceName: recources.find(res => res.id === activeEvent.resource)?.name || activeEvent.resource,
          activeEventDates: [activeEventStart.format(), activeEventEnd.format()],
          pendingEventDates: [pendingBookingStartDate.format(), pendingBookingEndDate.format()],
          occurrenceIndex: 0
        });
      }
    });

    return conflicts;
  }, [eventData, recources]);

  const findRecurringBookingConflicts = useCallback((): GroupedConflict[] => {
    const pendingBookings = eventData.filter(event => !event.active);

    if (!pendingBookings.length || !rule || !selectedRepetition || selectedRepetition.value === 'does_not_repeat') {
      return [];
    }

    const pendingBookingDuration = calculateDurationInHours(
      pendingBookings[0]?.start,
      pendingBookings[0]?.end
    );

    const allConflicts: ConflictDetail[] = [];

    pendingBookings.forEach(pendingBooking => {
      const pendingBookingStart = moment(pendingBooking.start);
      const pendingBookingAllOccurrences = new RRule({
        dtstart: pendingBookingStart.toDate(),
        freq: selectedRepetition.RRuleValue,
        count: selectedOccurances,
        byweekday: selectedRepetition.RRuleByweekday || undefined,
        bysetpos: selectedRepetition.RRuleBysetpos || undefined,
      }).all();

      const occurrencesWithConflicts = pendingBookingAllOccurrences.map((startDate, occurrenceIndex) => {
        const startMoment = moment(startDate);
        const endMoment = moment(startDate).clone().add(pendingBookingDuration, 'hours');

        const conflicts = isRecurringBookingConflicting(startMoment, endMoment, pendingBooking.resource);
        return conflicts.map(conflict => ({
          ...conflict,
          occurrenceIndex: occurrenceIndex + 1,
          resourceName: conflict.resourceName
        }));
      }).flat();

      allConflicts.push(...occurrencesWithConflicts);
    });

    const allConflictsWithNames: ConflictDetail[] = allConflicts.map(conflict => ({
      ...conflict,
      resourceName: recources.find(res => res.id === conflict.resource)?.name || conflict.resource
    }));

    return groupConflictsByOccurrence(allConflictsWithNames);
  }, [eventData, rule, selectedRepetition, selectedOccurances, recources, isRecurringBookingConflicting]);

  useEffect(() => {
    const foundConflicts = findRecurringBookingConflicts();
    if (foundConflicts) {
      setConflicts(foundConflicts);
    }
  }, [findRecurringBookingConflicts]);

  const resetAll = (): void => {
    setTemporaryStartDate(null);
    setTemporaryEndDate(null);
    setTemporarySelectedDates([]);
    setSelectedRepetition(null);
    setSelectedOccurances(2);
    setRule(null);
    setConflicts([]);
    setEventDataContext([]);
    setRecoursesContext([]);
  };

  const getTrimmedRRuleString = useCallback((timezone?: string): string | null => {
    if (!rule) return '';

    let rruleString = rule.toString();
    rruleString = rruleString.replace('RRULE:', '');
    if (timezone) {
      rruleString += `;TZID=${timezone}`;
    }
    return rruleString;
  }, [rule]);

  const value: RecurringBookingContextType = {
    isRecurringBooking,
    temporaryStartDate,
    temporaryEndDate,
    temporarySelectedDates,
    selectedRepetition,
    selectedOccurances,
    rule,
    conflicts,
    setTemporaryStartDate,
    setTemporaryEndDate,
    setSelectedRepetition,
    setSelectedOccurances,
    debouncedSetTempDates,
    setEventDataContext,
    setRecoursesContext,
    resetAll,
    getTrimmedRRuleString
  };

  return (
    <RecurringBookingContext.Provider value={value}>
      {children}
    </RecurringBookingContext.Provider>
  );
};

export const useRecurringBooking = (): RecurringBookingContextType => {
  const context = useContext(RecurringBookingContext);
  if (!context) {
    throw new Error('useRecurringBooking must be used within a RecurringBookingProvider');
  }
  return context;
};

export default RecurringBookingContext;

export type {
  RepetitionOption,
  ConflictDetail,
  GroupedConflict,
  EventData,
  ResourceData,
  RecurringBookingContextType,
  RecurringBookingProviderProps,
};
